import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { useGasPrice } from 'wagmi';
import { logger } from '@/lib/observability/logger';

/**
 * Gas units a wrap-and-deposit bundle is assumed to burn. Sized off measured p99 `gas_used` on
 * successful bundles: VaultV2 deposit 1.16M is the worst, repay 355k the cheapest. With the
 * buffer this reserves 1.8M units, ~1.55x that worst p99.
 * ponytail: fixed constant, so it cannot track a bundle that grows past 1.8M units. Retune from
 * PR #3846's `gas_used_vs_limit_percent` / `gas_used_vs_estimate_percent` telemetry.
 */
const WRAP_GAS_UNITS = 1_200_000n;

/** Buffer applied on top of the raw gas cost (150 = 1.5x). */
const GAS_RESERVE_BUFFER_PERCENT = 150n;
const PERCENT_DENOMINATOR = 100n;

/**
 * Native balance to hold back from a wrap-and-deposit so the wallet can still pay for it.
 * `gasUnits` defaults to the wrap bundle's burned-gas budget; gas-token callers pass a gas-limit
 * budget instead. `undefined` when the gas price cannot be read: `computeWrappableNativeBalance` maps that to
 * nothing wrappable, so an unreadable price disables wrapping instead of mis-sizing MAX.
 */
export function useWrappingGasReserve(t0) {
  const $ = _c(13);
  const {
    chainId,
    enabled,
    gasUnits: t1
  } = t0;
  const gasUnits = t1 === undefined ? WRAP_GAS_UNITS : t1;
  let t2;
  if ($[0] !== enabled) {
    t2 = {
      enabled,
      refetchInterval: 60000
    };
    $[0] = enabled;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== chainId || $[3] !== t2) {
    t3 = {
      chainId,
      query: t2
    };
    $[2] = chainId;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: gasPrice,
    isLoading,
    isError,
    error
  } = useGasPrice(t3);
  const lastLoggedError = useRef(null);
  let t4;
  let t5;
  if ($[5] !== chainId || $[6] !== error) {
    t4 = () => {
      if (!error || lastLoggedError.current === error) {
        return;
      }
      lastLoggedError.current = error;
      logger.error(error, {
        tags: {
          feature: "vaults.wrapping-gas-reserve"
        },
        extra: {
          chainId
        }
      });
    };
    t5 = [error, chainId];
    $[5] = chainId;
    $[6] = error;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const t6 = gasPrice !== undefined ? gasUnits * gasPrice * GAS_RESERVE_BUFFER_PERCENT / PERCENT_DENOMINATOR : undefined;
  const t7 = isError && gasPrice === undefined;
  let t8;
  if ($[9] !== isLoading || $[10] !== t6 || $[11] !== t7) {
    t8 = {
      reserve: t6,
      isLoading,
      isError: t7
    };
    $[9] = isLoading;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}